import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, HelpCircle, Star, Flame } from "lucide-react";
import { CelebrityCard } from "../components/CelebrityCard";
import { VersusBadge } from "../components/VersusBadge";
import { AuthButton } from "../components/AuthButton";
import { findCeleb } from "../data/celebrities";
import { getBestStreak, getStreak } from "../lib/streak";

const features = [
  { icon: Star, title: "Choose", body: "Pick between two icons." },
  { icon: Sparkles, title: "Explain", body: "Say what really decided it." },
  { icon: Star, title: "Predict", body: "Guess what the crowd will do." },
];

export default function Landing() {
  const nav = useNavigate();
  const a = findCeleb("c1");
  const b = findCeleb("c2");
  const best = getBestStreak();
  const current = getStreak();

  return (
    <div className="min-h-screen spotlight grain scanlines relative overflow-hidden">
      {/* Marquee stripe */}
      <div className="absolute top-0 inset-x-0 py-2 border-b border-white/10 bg-black/40 overflow-hidden z-10">
        <div className="marquee whitespace-nowrap font-display tracking-[0.3em] text-xs text-[#FF107A] flex">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="mx-6">
              ★ PREDICT · CHOOSE · REVEAL · CELEBRITY SMASH · DAILY 5 · NEW MATCHUPS ★
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 pt-16 pb-nav relative z-10">
        {/* Logo area */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-2" data-testid="app-logo">
            <img
              src="/brand/logo.png"
              alt="Celebrity Smash"
              className="h-10 sm:h-12 w-auto object-contain select-none"
              draggable={false}
              style={{ filter: "drop-shadow(0 0 12px rgba(255,16,122,0.35))" }}
            />
          </div>
          <button
            data-testid="how-it-works-link"
            onClick={() => nav("/how")}
            className="text-xs tracking-[0.2em] uppercase text-[#FDFBF7]/60 hover:text-[#00E5FF] flex items-center gap-1"
          >
            <HelpCircle size={14} /> How it works
          </button>
        </div>

        {/* Auth chip */}
        <div className="mt-4 flex justify-end">
          <AuthButton />
        </div>

        {/* Streak strip */}
        {(best > 0 || current > 0) && (
          <div
            data-testid="streak-strip"
            className="mt-4 rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/5 p-3 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center text-[#FFD700]">
              <Flame size={18} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#FFD700] font-bold">Solo high score</div>
              <div className="text-sm text-[#FDFBF7]/80">
                Best streak: <span data-testid="best-streak-value" className="font-display text-lg text-[#FDFBF7] tabular">{best}</span>
                {current > 0 && (
                  <span className="ml-3 text-[#FDFBF7]/60">Current: <span data-testid="current-streak-value" className="font-display text-lg text-[#FDFBF7] tabular">{current}</span></span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10"
        >
          <div className="text-[11px] tracking-[0.4em] uppercase text-[#00E5FF] font-bold flex items-center gap-2">
            <span className="w-6 h-px bg-[#00E5FF]" /> A NEW GAME SHOW
          </div>
          <h1 className="font-display text-[4.5rem] sm:text-8xl leading-[0.85] tracking-tight mt-4">
            PREDICT.
            <br />
            <span className="text-[#FF107A]">CHOOSE.</span>
            <br />
            <span className="text-outline-cream">REVEAL.</span>
          </h1>
          <p className="mt-5 text-[#FDFBF7]/75 text-base leading-relaxed max-w-sm">
            Two icons. One choice. The reason behind it reveals more than you think.
          </p>
        </motion.div>

        {/* Visual preview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-8 relative"
        >
          <div className="grid grid-cols-2 gap-3">
            <CelebrityCard celeb={a} testId="preview-celeb-a" />
            <CelebrityCard celeb={b} testId="preview-celeb-b" />
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
            <VersusBadge />
          </div>
        </motion.div>

        {/* CTAs */}
        <div className="mt-8 space-y-3">
          <button
            data-testid="hidden-prime-btn"
            onClick={() => nav("/challenge/new")}
            className="w-full rounded-full bg-[#FF107A] text-[#FDFBF7] font-bold tracking-wide py-4 text-lg flex items-center justify-center gap-2 glow-pink hover:brightness-110 active:scale-[0.98] transition-all"
          >
            Send a Hidden Prime <ArrowRight size={18} />
          </button>
          <button
            data-testid="play-today-btn"
            onClick={() => nav("/daily")}
            className="w-full rounded-full bg-transparent border-2 border-[#00E5FF] text-[#FDFBF7] font-bold tracking-wide py-4 hover:bg-[#00E5FF]/10 transition-all"
          >
            Play Today&rsquo;s Daily Smash
          </button>
          <button
            data-testid="power-smash-btn"
            onClick={() => nav("/power")}
            className="w-full rounded-full font-bold tracking-wide py-4 text-lg text-[#0A090D] flex items-center justify-center gap-2 hover:brightness-110 active:scale-[0.98] transition-all"
            style={{
              background: "linear-gradient(90deg, #FFD700 0%, #FF6B35 50%, #FF107A 100%)",
              boxShadow: "0 0 24px rgba(255,215,0,0.35), 0 0 40px rgba(255,107,53,0.25)",
            }}
          >
            ⚡ Power Smash <ArrowRight size={18} />
          </button>
          <button
            data-testid="create-custom-btn"
            onClick={() => nav("/create")}
            className="w-full rounded-full bg-transparent border-2 border-[#A855F7]/50 text-[#FDFBF7]/85 font-medium tracking-wide py-3 hover:bg-[#A855F7]/10 transition-all text-sm"
          >
            Create a Custom Smash
          </button>
        </div>

        {/* Feature callouts */}
        <div className="mt-10 grid grid-cols-3 gap-2">
          {features.map(({ icon: Icon, title, body }, i) => (
            <div
              key={title}
              className="rounded-2xl border border-white/10 bg-black/30 p-3 hover:border-[#00E5FF]/40 transition-colors"
              data-testid={`feature-${title.toLowerCase()}`}
            >
              <div className="flex items-center gap-1.5">
                <Icon size={14} className="text-[#00E5FF] twinkle" style={{ animationDelay: `${i * 0.3}s` }} />
                <span className="text-[9px] tracking-[0.25em] uppercase text-[#FDFBF7]/50 font-bold">0{i + 1}</span>
              </div>
              <div className="font-display text-2xl mt-2 tracking-wide leading-none">{title}</div>
              <p className="text-[11px] text-[#FDFBF7]/60 leading-snug mt-1">{body}</p>
            </div>
          ))}
        </div>

        {/* Bottom insight */}
        <div className="mt-8 rounded-2xl border border-[#A855F7]/30 bg-[#A855F7]/5 p-4">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#A855F7] font-bold">
            The core idea
          </div>
          <p className="mt-2 text-sm text-[#FDFBF7]/85 leading-relaxed">
            The reason behind the choice is more revealing than the choice itself.
          </p>
        </div>
      </div>
    </div>
  );
}
